import React, { useEffect, useState } from 'react'
import {
	BoltIcon,
	CheckCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'

import useGlobalTheme from '../../hooks/useGlobalTheme'

function LiveLoadFetchingModal({ onEnable, onClose }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [dontShowAgain, setDontShowAgain] = useState(false)

	useEffect(() => {
		const onKey = event => {
			if (event.key === 'Escape') onClose?.(dontShowAgain)
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [dontShowAgain, onClose])

	return (
		<div
			className="fixed inset-0 z-[70] flex items-start justify-center bg-black/70 pt-32 px-4"
			role="dialog"
			aria-modal="true"
			aria-label="Enable live load fetching"
			onClick={() => onClose?.(dontShowAgain)}
		>
			<div
				className={`w-full max-w-xl rounded-2xl overflow-hidden shadow-xl ${
					isLight ? 'bg-white' : 'bg-[#15202b] ring-1 ring-white/10'
				}`}
				onClick={event => event.stopPropagation()}
			>
				<div
					className={`flex items-center gap-2 px-5 py-3 border-b ${
						isLight
							? 'bg-emerald-50 border-emerald-100 text-emerald-700'
							: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
					}`}
				>
					<CheckCircleIcon className="h-5 w-5 shrink-0" />
					<span className="text-sm font-medium">
						LoadLine extension connected
					</span>
					<button
						type="button"
						onClick={() => onClose?.(dontShowAgain)}
						aria-label="Close"
						className={`ml-auto -mr-1.5 p-1 rounded-md transition-colors ${
							isLight ? 'hover:bg-emerald-100' : 'hover:bg-emerald-500/20'
						}`}
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<div className="px-6 pt-6 pb-6 flex items-start gap-4">
					<div
						className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${
							isLight ? 'bg-sky-50' : 'bg-sky-500/15'
						}`}
					>
						<BoltIcon className={`h-6 w-6 ${isLight ? 'text-sky-600' : 'text-sky-400'}`} />
					</div>
					<div className="min-w-0">
						<h2 className={`text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-gray-100'}`}>
							Get loads faster
						</h2>
						<p
							className={`text-sm mt-2 leading-relaxed ${
								isLight ? 'text-gray-600' : 'text-gray-400'
							}`}
						>
							Enable live load fetching to get loads faster for your active
							search.
						</p>
					</div>
				</div>

				<div
					className={`px-6 py-4 border-t ${
						isLight ? 'bg-gray-50 border-gray-100' : 'bg-white/[0.03] border-white/[0.06]'
					}`}
				>
					<label
						className={`mb-4 flex cursor-pointer items-center gap-2 text-sm ${
							isLight ? 'text-gray-600' : 'text-gray-400'
						}`}
					>
						<input
							type="checkbox"
							checked={dontShowAgain}
							onChange={event => setDontShowAgain(event.target.checked)}
							className="h-4 w-4 rounded border-gray-300 text-sky-500 focus:ring-sky-500"
						/>
						Don’t show this again
					</label>
					<div className="flex items-center justify-end gap-3">
						<button
							type="button"
							onClick={() => onClose?.(dontShowAgain)}
							className={`px-4 py-2 text-sm font-medium transition-colors ${
								isLight
									? 'text-gray-600 hover:text-gray-900'
									: 'text-gray-400 hover:text-gray-200'
							}`}
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={() => onEnable?.(dontShowAgain)}
							className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-sky-500 text-white hover:bg-sky-600 transition-colors"
						>
							<BoltIcon className="h-4 w-4" />
							Enable
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default LiveLoadFetchingModal
